import React, { useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Box, Chip, Tooltip, Typography } from '@mui/material'

import CustomTable from '../../components/CustomTable'
import type { ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import { SisSyncManager } from '../../APIManager'
import { formatRunDuration } from '../../helpers/sisSyncRunDisplay'
import type { ISisSyncRun, ISisSyncRunFile, ISisSyncRunFileListResponse, SisSyncRunFileListParams } from '../../models/SisSyncRun'
import { notifyError } from '../../utils/notifyError'
import { getFileStatusChip, getFileWarningsLabel, toFileTableKeys } from './runFiles'
import { RUNNING_REFETCH_MS } from './runSummary'

const FILES_TITLE = 'Files'
const PAGE_SIZE = 10

type FileRow = Omit<ISisSyncRunFile, 'taskMetadata'>

// Matches the run header beside it: a scheduled run has not started, a running one has not reached
// a file yet, and any other run ended before the first file.
const getEmptyFilesSentence = (run: ISisSyncRun): string => {
  if (run.outcome === 'SCHEDULED') return 'This run has not started yet.'
  if (run.outcome === 'RUNNING') return 'No files have been imported yet.'
  return 'No files were imported. The sync stopped before it reached the first file.'
}

interface SisSyncRunFilesTableProps {
  run: ISisSyncRun
}

export const SisSyncRunFilesTable = ({ run }: SisSyncRunFilesTableProps): JSX.Element => {
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ pageNumber: 1, pageSize: PAGE_SIZE })

  const requestParams = useMemo((): SisSyncRunFileListParams => ({
    pageNumber: params.pageNumber,
    pageSize: params.pageSize,
    search: params.search,
    sort: typeof params.sort === 'string' ? params.sort : undefined
  }), [params.pageNumber, params.pageSize, params.search, params.sort])

  const { data: fetchedFiles, isLoading, isPlaceholderData, isError, error } = useQuery({
    // outcome is in the key so the rows re-read once when the run finishes: the last running
    // fetch can still hold a Running file that has since finished.
    queryKey: ['sisSyncRunFiles', run.sisSyncRunId, run.outcome, requestParams],
    queryFn: async (): Promise<ISisSyncRunFileListResponse> =>
      (await SisSyncManager.getSisSyncRunFiles(run.sisSyncRunId, requestParams)).data,
    // A new page or sort keeps the current rows on screen until it arrives, rather than a spinner.
    // The chips read each row's own status, so kept rows never take on a newer run outcome.
    placeholderData: keepPreviousData,
    // The table shows its own error line and the page notifies once.
    meta: { silent: true },
    refetchInterval: run.outcome === 'RUNNING' ? RUNNING_REFETCH_MS : false
  })

  useEffect(() => {
    if (isError) notifyError(error, { fallbackMessage: 'Failed to load the files of this SIS sync run. Please try again.' })
  }, [isError])

  const response = fetchedFiles

  const list = useMemo(() => {
    if (response == null) return undefined
    return {
      ...response,
      // CustomTable's row tooltip reads taskMetadata, and would only repeat the count columns.
      data: response.data.map(({ data: { taskMetadata, ...file }, ...row }) => ({ ...row, data: file })),
      keys: toFileTableKeys(response.keys)
    }
  }, [response])

  const overrides = {
    status: (file: FileRow) => {
      const { label, color, variant } = getFileStatusChip(file)
      const warnings = getFileWarningsLabel(file)
      return (
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title={file.errorMessage ?? ''} placement="top" arrow>
            {/* Focusable when it has a message, so keyboard users can open the tooltip too. */}
            <Chip size="small" label={label} color={color} variant={variant} tabIndex={file.errorMessage != null ? 0 : undefined} />
          </Tooltip>
          {warnings != null && <Chip size="small" label={warnings} color="warning" variant="outlined" />}
        </Box>
      )
    },
    durationMs: (file: FileRow) => (
      <Typography>{file.durationMs != null ? formatRunDuration(file.durationMs) : '—'}</Typography>
    )
  }

  if (isError && list == null) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography fontWeight={600} fontSize="18px">{FILES_TITLE}</Typography>
        <Typography variant="body1">The files of this sync run could not be loaded.</Typography>
      </Box>
    )
  }

  // Only a real answer for the current search: kept rows from a previous no-match search are not
  // "this run has no files", and returning early here would also unmount the table and its sort.
  if (list != null && !isPlaceholderData && list.pagination.totalResults === 0 && (params.search ?? '') === '') {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography fontWeight={600} fontSize="18px">{FILES_TITLE}</Typography>
        <Typography variant="body1">
          {getEmptyFilesSentence(run)}
        </Typography>
      </Box>
    )
  }

  return (
    <CustomTable
      list={list}
      rows={list?.data ?? []}
      loading={isLoading}
      topbarTitle={FILES_TITLE}
      overrides={overrides}
      featureName={FILES_TITLE}
      // Eleven columns at the shared 160 px minimum scroll sideways on a laptop; counts are short.
      extraColumnProps={{ minWidth: 90 }}
      hideActionsColumn
      disableRowDoubleClick
      params={params}
      setParams={setParams}
    />
  )
}
